"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import { calculateDiscountBreakdown, hasActiveDiscount } from "@/lib/discount/calculateDiscount";

interface StayPriceBreakdownCardProps {
  /** Current nightly rate in ZAR (discounted if discount active) */
  nightlyRate: number;
  /** Number of nights being booked */
  nights: number;
  /** Optional original nightly rate for strikethrough (if discounted) */
  originalNightlyRate?: number;
  /** Optional discount percentage (0-100) - legacy support */
  discountPercent?: number;
  /** Show the deposit split */
  showDepositSplit?: boolean;
  /** Deposit percent charged at checkout (default 10). Clamped to 5-100 display. */
  depositPercent?: number;
  /** One-time cleaning fee in ZAR (default 0). Added to the booking gross. */
  cleaningFeeZar?: number;
  /** Compact variant for inline display */
  compact?: boolean;
  /** Custom title */
  title?: string;
  /**
   * G6: the host's seasons resolved against the guest's chosen dates — from
   * staySeasonalRates.averageSeasonalNightlyRate. When present the quoted
   * rate IS the seasonal average (matches the checkout snapshot exactly);
   * the per-night span is shown instead of a flat rate times nights.
   */
  seasonal?: {
    averageNightlyRate: number;
    cheapestNight: number;
    dearestNight: number;
  } | null;
}

export function StayPriceBreakdownCard({
  nightlyRate,
  nights,
  originalNightlyRate,
  discountPercent = 0,
  showDepositSplit = true,
  depositPercent = 10,
  cleaningFeeZar = 0,
  compact = false,
  title = "Pricing Breakdown",
  seasonal = null,
}: StayPriceBreakdownCardProps) {
  // G6: when seasons touch these dates the quoted rate IS the seasonal
  // average — discount mechanics step aside (seasons supersede discounts).
  const seasonalActive = Boolean(seasonal);
  // Determine if we have an active discount
  const hasDiscount = hasActiveDiscount(nightlyRate, originalNightlyRate, true) || discountPercent > 0;
  
  const normalizedDepositPercent = Math.min(100, Math.max(5, Math.round(depositPercent || 10)));
  const normalizedCleaningFee = Number.isFinite(cleaningFeeZar) && cleaningFeeZar > 0 ? Math.round(cleaningFeeZar * 100) / 100 : 0;
  const balancePercent = 100 - normalizedDepositPercent;

  // Use discount breakdown calculation if discount is active
  let breakdown;
  if (hasDiscount && originalNightlyRate && originalNightlyRate > nightlyRate) {
    breakdown = calculateDiscountBreakdown(originalNightlyRate, nightlyRate, nights);
  } else if (discountPercent > 0) {
    // Legacy: use discountPercent
    const subtotal = nightlyRate * nights;
    const discountAmount = (subtotal * discountPercent) / 100;
    const total = subtotal - discountAmount;
    breakdown = {
      originalRate: nightlyRate,
      discountedRate: nightlyRate,
      discountPercent,
      savingsPerNight: 0,
      nights,
      originalTotal: subtotal,
      discountedTotal: total,
      savings: discountAmount,
      deposit10Percent: Math.round((total * 0.1) * 100) / 100,
      balance90Percent: Math.round((total * 0.9) * 100) / 100,
    };
  } else {
    // No discount
    const subtotal = nightlyRate * nights;
    breakdown = {
      originalRate: nightlyRate,
      discountedRate: nightlyRate,
      discountPercent: 0,
      savingsPerNight: 0,
      nights,
      originalTotal: subtotal,
      discountedTotal: subtotal,
      savings: 0,
      deposit10Percent: Math.round((subtotal * 0.1) * 100) / 100,
      balance90Percent: Math.round((subtotal * 0.9) * 100) / 100,
    };
  }

  const staySubtotal = breakdown.discountedTotal;
  const bookingGross = Math.round((staySubtotal + normalizedCleaningFee) * 100) / 100;
  const depositAmount = Math.round((bookingGross * normalizedDepositPercent) / 100 * 100) / 100;
  const balanceAmount = Math.round((bookingGross - depositAmount) * 100) / 100;

  if (compact) {
    return (
      <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">
            {nights} night{nights !== 1 ? "s" : ""} × R{breakdown.discountedRate.toFixed(2)}
          </span>
          {breakdown.savings > 0 && (
            <span className="line-through text-slate-400">R{breakdown.originalTotal.toFixed(2)}</span>
          )}
          <span className="font-semibold">R{staySubtotal.toFixed(2)}</span>
        </div>

        {seasonalActive && seasonal && (
          <p className="text-xs text-muted-foreground">
            Seasonal prices included — these dates span R{seasonal.cheapestNight.toFixed(0)}
            {" "}to R{seasonal.dearestNight.toFixed(0)} per night.
          </p>
        )}
        {!seasonalActive && (
          <p className="text-xs text-muted-foreground">
            Direct price — no booking commission added. Booked on Zamzi = the lowest total.
          </p>
        )}

        {breakdown.savings > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-emerald-600 font-semibold">
              Savings ({breakdown.discountPercent}%)
            </span>
            <span className="font-semibold text-emerald-600">
              -R{breakdown.savings.toFixed(2)}
            </span>
          </div>
        )}

        {normalizedCleaningFee > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Cleaning fee (one-time)</span>
            <span className="font-semibold">R{normalizedCleaningFee.toFixed(2)}</span>
          </div>
        )}

        <div className="border-t pt-2 flex justify-between">
          <span className="font-semibold text-slate-900">Total</span>
          <span className="font-bold text-lg text-slate-900">R{bookingGross.toFixed(2)}</span>
        </div>

        {showDepositSplit && (
          <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
            <p className="text-xs text-blue-900 font-semibold">Payment Split</p>
            <div className="grid grid-cols-2 gap-2 mt-1 text-xs">
              <div>
                <p className="text-blue-700">Pay Now ({normalizedDepositPercent}%)</p>
                <p className="font-bold text-blue-900">R{depositAmount.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-blue-700">After Arrival ({balancePercent}%)</p>
                <p className="font-bold text-blue-900">R{balanceAmount.toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className="border-blue-200 bg-blue-50/50">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>
          {nights} night{nights !== 1 ? "s" : ""} at R{breakdown.discountedRate.toFixed(2)}/night
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2 text-sm">
          {breakdown.savings > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Original price:</span>
              <span className="line-through text-slate-400 font-semibold">R{breakdown.originalTotal.toFixed(2)}</span>
            </div>
          )}

          <div className="flex justify-between">
            <span className="text-slate-600">{breakdown.nights} nights × R{breakdown.discountedRate.toFixed(2)}</span>
            <span className="font-semibold">R{staySubtotal.toFixed(2)}</span>
          </div>

          {breakdown.savings > 0 && (
            <div className="flex justify-between text-emerald-600 bg-emerald-50 p-2 rounded">
              <span className="font-semibold">
                You save ({breakdown.discountPercent}%)
              </span>
              <span className="font-bold">-R{breakdown.savings.toFixed(2)}</span>
            </div>
          )}

          {normalizedCleaningFee > 0 && (
            <div className="flex justify-between">
              <span className="text-slate-600">Cleaning fee (one-time)</span>
              <span className="font-semibold">R{normalizedCleaningFee.toFixed(2)}</span>
            </div>
          )}

          <div className="border-t pt-2 flex justify-between text-base">
            <span className="font-semibold">Total Amount</span>
            <span className="font-bold text-lg text-slate-900">R{bookingGross.toFixed(2)}</span>
          </div>
        </div>

        {showDepositSplit && (
          <div className="rounded-lg border border-blue-300 bg-white p-4">
            <div className="flex items-start gap-2 mb-3">
              <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-sm text-blue-900">Zamzi {normalizedDepositPercent}/{balancePercent} Protection</p>
                <p className="text-xs text-blue-700 mt-0.5">
                  Secure your booking now, complete payment after safe arrival.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="border-l-2 border-blue-500 pl-4">
                <p className="text-xs text-slate-600 font-semibold uppercase">Pay Now ({normalizedDepositPercent}%)</p>
                <p className="text-lg font-bold text-slate-900 mt-1">
                  R{depositAmount.toFixed(2)}
                </p>
                <p className="text-xs text-slate-500 mt-1">via PayFast</p>
              </div>
              <div className="border-l-2 border-slate-300 pl-4">
                <p className="text-xs text-slate-600 font-semibold uppercase">Pay on Arrival ({balancePercent}%)</p>
                <p className="text-lg font-bold text-slate-900 mt-1">
                  R{balanceAmount.toFixed(2)}
                </p>
                <p className="text-xs text-slate-500 mt-1">After safe arrival check</p>
              </div>
            </div>
          </div>
        )}

        {showDepositSplit && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs text-amber-900 font-semibold">⚠️ Payment Safety</p>
            <p className="text-xs text-amber-800 mt-1">
              Always pay through PayFast only. Never hand cash or make direct payments to the host outside of PayFast.
              This protects you with Zamzi's full booking protection.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
